import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { AuthService } from '../../../services/auth/auth.service';
import { AccessGroupDTO } from '../../../dto/access-group/access-group.dto';

interface OrganizationOption {
  id: number;
  org_name: string;
}

interface CprngTestUserOrgBinding {
  users_id: number;
  full_name: string;
  organizations_id: number | null;
  organizations_users_found: boolean;
}

interface CprngTestUserOrgBindingsResponse {
  identity_types_id: number;
  identity_types_organizations_id: number | null;
  items: CprngTestUserOrgBinding[];
}

@Component({
  selector: 'app-duna-cprng-access-groups',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatProgressBarModule,
  ],
  templateUrl: './duna-cprng-access-groups.component.html',
  styleUrl: './duna-cprng-access-groups.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaCprngAccessGroupsComponent implements OnInit, OnDestroy {
  allowed = false;
  loading = false;
  errorMessage: string | null = null;

  catalog: AccessGroupDTO[] = [];
  catalogColumns = [
    'clean_name',
    'display_name',
    'enabled_by_default',
    'sort_order',
    'actions',
  ];

  organizations: OrganizationOption[] = [];
  organizationsError: string | null = null;
  selectedOrgId: number | null = null;
  orgMatrix: AccessGroupDTO[] = [];

  testUserBindings: CprngTestUserOrgBinding[] = [];
  testUserBindingsError: string | null = null;
  testUserBindingsLoading = false;
  identityTypesId: number | null = null;
  identityTypesOrganizationsId: number | null = null;
  pendingTestUsersOrgId: number | null = null;
  savingTestUsers = false;

  newGroup = {
    clean_name: '',
    display_name: '',
    description: '',
    enabled_by_default: false,
    sort_order: 0,
  };

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.allowed = (roles || []).map((r) => r.toLowerCase()).includes('superadmin');
        this.cdr.markForCheck();
        if (this.allowed) {
          this.loadCatalog();
          this.loadOrganizations();
          this.loadTestUserBindings();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCatalog(): void {
    this.loading = true;
    this.errorMessage = null;
    this.http
      .get<{ items: AccessGroupDTO[] }>(`${environment.apiUrl}/access-groups`, {
        withCredentials: true,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.catalog = res.items ?? [];
        },
        error: () => {
          this.errorMessage = 'Kunne ikke hente adgangsgrupper';
        },
      });
  }

  loadOrganizations(): void {
    this.organizationsError = null;
    this.http
      .get<OrganizationOption[]>(`${environment.apiUrl}/organizations`, {
        withCredentials: true,
      })
      .subscribe({
        next: (orgs) => {
          this.organizations = [...(orgs ?? [])].sort((a, b) =>
            a.org_name.localeCompare(b.org_name, 'da')
          );
          if (this.organizations.length && this.selectedOrgId == null) {
            this.selectedOrgId = this.organizations[0].id;
            this.loadOrgMatrix();
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.organizationsError = 'Kunne ikke hente organisationer.';
          this.cdr.markForCheck();
        },
      });
  }

  loadOrgMatrix(): void {
    if (this.selectedOrgId == null) {
      return;
    }
    this.loading = true;
    this.http
      .get<{ items: AccessGroupDTO[] }>(
        `${environment.apiUrl}/organizations/${this.selectedOrgId}/access-groups`,
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.orgMatrix = res.items ?? [];
        },
        error: () => {
          this.snackBar.open('Kunne ikke hente org-indstillinger', 'Luk', {
            duration: 4000,
          });
        },
      });
  }

  onOrgChange(): void {
    this.loadOrgMatrix();
  }

  orgNameById(orgId: number | null | undefined): string {
    if (orgId == null) {
      return '—';
    }
    const org = this.organizations.find((o) => o.id === orgId);
    return org?.org_name ?? `#${orgId}`;
  }

  loadTestUserBindings(): void {
    this.testUserBindingsError = null;
    this.testUserBindingsLoading = true;
    this.http
      .get<CprngTestUserOrgBindingsResponse>(
        `${environment.apiUrl}/cprng/test-users/org-bindings`,
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.testUserBindingsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.identityTypesId = res.identity_types_id ?? null;
          this.identityTypesOrganizationsId =
            res.identity_types_organizations_id ?? null;
          this.testUserBindings = res.items ?? [];
          this.syncPendingTestUsersOrgId(res);
        },
        error: () => {
          this.testUserBindingsError = 'Kunne ikke hente testbruger-org';
        },
      });
  }

  private syncPendingTestUsersOrgId(res: CprngTestUserOrgBindingsResponse): void {
    const fromIdentity = res.identity_types_organizations_id;
    if (fromIdentity != null) {
      this.pendingTestUsersOrgId = fromIdentity;
      return;
    }
    const fromUser = res.items?.find((r) => r.organizations_id != null)?.organizations_id;
    if (fromUser != null) {
      this.pendingTestUsersOrgId = fromUser;
      return;
    }
    if (this.organizations.length) {
      this.pendingTestUsersOrgId = this.organizations[0].id;
    }
  }

  allTestUsersOnPendingOrg(): boolean {
    if (this.pendingTestUsersOrgId == null) {
      return false;
    }
    const target = this.pendingTestUsersOrgId;
    if (this.identityTypesOrganizationsId !== target) {
      return false;
    }
    return this.testUserBindings.every(
      (r) =>
        r.organizations_users_found && r.organizations_id === target
    );
  }

  applyTestUsersOrganization(): void {
    const targetOrgId = this.pendingTestUsersOrgId;
    if (targetOrgId == null) {
      this.snackBar.open('Vælg en organisation', 'Luk', { duration: 3000 });
      return;
    }
    if (this.allTestUsersOnPendingOrg()) {
      this.snackBar.open('Alle testbrugere og identity_types er allerede på den org', 'Luk', {
        duration: 3000,
      });
      return;
    }

    this.savingTestUsers = true;
    this.http
      .patch<CprngTestUserOrgBindingsResponse>(
        `${environment.apiUrl}/cprng/test-users/organization`,
        { organizations_id: targetOrgId },
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.savingTestUsers = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.identityTypesId = res.identity_types_id ?? null;
          this.identityTypesOrganizationsId =
            res.identity_types_organizations_id ?? null;
          this.testUserBindings = res.items ?? [];
          this.pendingTestUsersOrgId =
            res.identity_types_organizations_id ?? targetOrgId;
          this.snackBar.open(
            `Testbrugere 4–8 flyttet til ${this.orgNameById(targetOrgId)} (identity_types #11 opdateret)`,
            'Luk',
            { duration: 4000 }
          );
        },
        error: (err) => {
          const msg =
            err?.error?.message || 'Kunne ikke opdatere testbruger-org';
          this.snackBar.open(msg, 'Luk', { duration: 5000 });
        },
      });
  }

  createGroup(): void {
    const clean = this.newGroup.clean_name.trim().toLowerCase();
    const display = this.newGroup.display_name.trim();
    if (!clean || !display) {
      this.snackBar.open('clean_name og display_name er påkrævet', 'Luk', {
        duration: 4000,
      });
      return;
    }
    this.loading = true;
    this.http
      .post<AccessGroupDTO>(
        `${environment.apiUrl}/access-groups`,
        {
          clean_name: clean,
          display_name: display,
          description: this.newGroup.description || null,
          enabled_by_default: this.newGroup.enabled_by_default,
          sort_order: this.newGroup.sort_order,
        },
        { withCredentials: true }
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.newGroup = {
            clean_name: '',
            display_name: '',
            description: '',
            enabled_by_default: false,
            sort_order: 0,
          };
          this.loadCatalog();
          if (this.selectedOrgId != null) {
            this.loadOrgMatrix();
          }
          this.snackBar.open('Adgangsgruppe oprettet', 'Luk', { duration: 3000 });
        },
        error: (err) => {
          const msg =
            err?.error?.message || 'Kunne ikke oprette adgangsgruppe';
          this.snackBar.open(msg, 'Luk', { duration: 5000 });
        },
      });
  }

  deleteGroup(row: AccessGroupDTO): void {
    if (!confirm(`Slet adgangsgruppen "${row.display_name}"?`)) {
      return;
    }
    this.http
      .delete(`${environment.apiUrl}/access-groups/${row.id}`, {
        withCredentials: true,
      })
      .subscribe({
        next: () => {
          this.loadCatalog();
          if (this.selectedOrgId != null) {
            this.loadOrgMatrix();
          }
        },
        error: () => {
          this.snackBar.open('Kunne ikke slette gruppe', 'Luk', { duration: 4000 });
        },
      });
  }

  toggleOrgGroup(row: AccessGroupDTO, enabled: boolean): void {
    if (this.selectedOrgId == null) {
      return;
    }
    this.http
      .patch<{ items: AccessGroupDTO[] }>(
        `${environment.apiUrl}/organizations/${this.selectedOrgId}/access-groups`,
        { access_group_id: row.id, is_enabled: enabled },
        { withCredentials: true }
      )
      .subscribe({
        next: (res) => {
          this.orgMatrix = res.items ?? this.orgMatrix;
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open('Kunne ikke opdatere org-indstilling', 'Luk', {
            duration: 4000,
          });
        },
      });
  }
}
